import React from "react";
import useUnderWaterWorkPermitForm from "../../../hooks/useUnderWaterWorkPermitForm";
import { PermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import LoadingSpinner from "../../Common/LoadingSpinner";
import PermitHeader from "./PermitHeader";
import { Location, PermitType, WorkOrigin } from "../../../common/enums";
import DangerAlert from "../../Common/DangerAlert";
import PermitActionBar from "./PermitActionBar";
import RadioButton from "../../Common/RadioButton";
import TextInput from "../../Common/TextInput";
import SearchDropdown from "../../Common/SearchDropdown";
import DateTimeInput from "../../Common/DateTimeInput";
import RemindMe from "./RemindMe";
import LabelCheckbox from "../../Common/LabelCheckbox";
import TextArea from "../../Common/TextArea";
import ParticipantsList from "./ParticipantsList";
import AdminComment from "./AdminComment";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import Button from "../../Common/Button";
import Link from "../../Common/Link";
import SignatureModal from "../../Signatures/SignatureModal";
import AcceptUnsavedChangesModal from "./AcceptUnsavedChangesModal";
import RejectPermitModal from "../RejectPermitModal";
import PositionPicker from "./PositionPicker";
import resourceUtils from "../../../common/resourceUtils";

function UnderWaterWorkPermitForm(props: PermitFormProps) {
    const formManager = useUnderWaterWorkPermitForm(props);

    const { formState, storedPermit, department, formStateChangeHandler } = formManager;
    const { resources } = props;

    if (formManager.isLoading) {
        return <LoadingSpinner />;
    }

    const permitNameResource = resourceUtils.getPermitNameResource(
        PermitType.UnderWaterWorkPermit,
        formManager.contentVersion,
        resources
    );

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            permitDocument={storedPermit}
                            permitNameResource={permitNameResource}
                            permitType={PermitType.UnderWaterWorkPermit}
                            resources={resources}
                        />
                    </div>

                    {!!storedPermit && formManager.showRejectionComment ? (
                        <DangerAlert>{storedPermit.rejectionComment}</DangerAlert>
                    ) : null}

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    <form onSubmit={formManager.submitHandler}>
                        <div className="d-flex mt-3">
                            <span className="fw-bold me-2">{resources.get("WorkLocation")}</span>
                            <RadioButton
                                checked={formState.workLocation.value === Location.General}
                                inputId="work-location__general"
                                label={resources.get("General")}
                                name="work-location"
                                onSelect={() => formManager.formStateChangeHandler("workLocation", Location.General)}
                                containerClassName="me-3"
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={formState.workLocation.value === Location.EnergyHarbour}
                                inputId="work-location__energy-harbour"
                                label={resources.get("EnergyHarbour")}
                                name="work-location"
                                onSelect={() =>
                                    formManager.formStateChangeHandler("workLocation", Location.EnergyHarbour)
                                }
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <h1 className="h6">{resources.get("Conditions0")}</h1>
                            <p>{resources.get("Conditions1")}</p>

                            <h1 className="h6">{resources.get("Conditions2")}</h1>
                            <p>{resources.get("Conditions3")}</p>

                            <h1 className="h6">{resources.get("Conditions4")}</h1>
                            <ul>
                                <li>{resources.get("Conditions5")}</li>
                                <li>{resources.get("Conditions6")}</li>
                                <li>{resources.get("Conditions7")}</li>
                                <li>{resources.get("Conditions8")}</li>
                                <li>{resources.get("Conditions9")}</li>
                                <li>{resources.get("Conditions10")}</li>
                                <li>{resources.get("Conditions11")}</li>
                                <li>{resources.get("Conditions12")}</li>
                                <li>{resources.get("Conditions13")}</li>
                            </ul>
                        </div>

                        {formManager.showEnergyHarbourConditions ? (
                            <div
                                className="p-3 rounded mt-3 mb-3"
                                style={{
                                    backgroundColor: "#FFF9EA"
                                }}
                            >
                                <h6>{resources.get("DivingPermitEHAreProvidedOnTerms")}</h6>
                                <p>{resources.get("WorkPermitEHCanBeCreatedBy")}</p>
                                <TextInput
                                    containerClassName={"w-50"}
                                    inputId="energy-harbour-permit-sequence-identifier"
                                    labelText={resources.get("IfAnEHPermitAlreadyExists")}
                                    onChange={(s) => formStateChangeHandler("energyHarbourSequenceIdentifier", s)}
                                    value={formState.energyHarbourSequenceIdentifier.value}
                                    errorMessage={formState.energyHarbourSequenceIdentifier.errorMessage}
                                    readOnly={!formManager.canManageContent}
                                />
                                {formManager.canManageContent ? (
                                    <SearchDropdown
                                        inputId="energy-harbour-permit-search-input"
                                        searchLabel={resources.get("SearchLabel")}
                                        placeHolder={resources.get("SearchPlaceholder")}
                                        handler={formManager.energyHarbourPermitSearchHandler}
                                        noSearchResultsText={resources.get("NoSearchResults")}
                                        ref={formManager.energyHarbourPermitSearchHandler.searchSectionRef}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositions}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                id="end-date"
                                label={resources.get("EndDateTime")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                minValue={formState.startDate.value}
                                ref={formManager.endDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneur.value}
                                errorMessage={formState.entrepreneur.errorMessage}
                                inputId="entrepreneur"
                                labelText={props.resources.get("Entrepreneur")}
                                onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.companyOrgNr.value}
                                errorMessage={formState.companyOrgNr.errorMessage}
                                inputId="company-org-nr"
                                labelText={props.resources.get("CompanyOrgNr")}
                                onChange={(s) => formStateChangeHandler("companyOrgNr", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                value={formState.description.value}
                                errorMessage={formState.description.errorMessage}
                                labelText={resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                textAreaId="description"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.responsibleWorkLeader.name}
                                errorMessage={formState.responsibleWorkLeader.nameErrorMessage}
                                inputId="responsible-work-leader__name"
                                labelText={props.resources.get("ResponsibleWorkLeader")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("responsibleWorkLeader", {
                                        ...formState.responsibleWorkLeader,
                                        name: s
                                    })
                                }
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.responsibleWorkLeader.phoneNumber}
                                errorMessage={formState.responsibleWorkLeader.phoneNumberErrorMessage}
                                inputId="responsible-work-leader__phone-number"
                                labelText={props.resources.get("ResponsibleWorkLeaderPhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("responsibleWorkLeader", {
                                        ...formState.responsibleWorkLeader,
                                        phoneNumber: s
                                    })
                                }
                                containerClassName="col-md-6"
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.projectLeader.name}
                                errorMessage={formState.projectLeader.nameErrorMessage}
                                inputId="project-leader__name"
                                labelText={props.resources.get("ProjectLeader")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        name: s
                                    })
                                }
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.projectLeader.phoneNumber}
                                errorMessage={formState.projectLeader.phoneNumberErrorMessage}
                                inputId="project-leader__phone-number"
                                labelText={props.resources.get("ProjectLeaderPhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        phoneNumber: s
                                    })
                                }
                                containerClassName="col-md-6"
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="d-flex justify-content-between mt-3">
                            <LabelCheckbox
                                checked={formState.vhfIsAvailable.value}
                                id="vhf-is-available"
                                label={resources.get("VHFIsAvailable")}
                                onChange={(c) => formStateChangeHandler("vhfIsAvailable", c)}
                                labelClassName="fw-bold"
                                readOnly={!formManager.canManageContent}
                            />

                            <div className="d-flex">
                                <span className="fw-bold me-2">{resources.get("WorkOrigin")}</span>
                                <RadioButton
                                    checked={formState.workOrigin.value === WorkOrigin.Quay}
                                    inputId="work-origin__quay"
                                    label={resources.get("Quay")}
                                    name="work-origin"
                                    onSelect={() => formManager.formStateChangeHandler("workOrigin", WorkOrigin.Quay)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                                <RadioButton
                                    checked={formState.workOrigin.value === WorkOrigin.Boat}
                                    inputId="work-origin__boat"
                                    label={resources.get("Boat")}
                                    name="work-origin"
                                    onSelect={() => formManager.formStateChangeHandler("workOrigin", WorkOrigin.Boat)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                                <RadioButton
                                    checked={formState.workOrigin.value === WorkOrigin.Raft}
                                    inputId="work-origin__raft"
                                    label={resources.get("Raft")}
                                    name="work-origin"
                                    onSelect={() => formManager.formStateChangeHandler("workOrigin", WorkOrigin.Raft)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                                <RadioButton
                                    checked={formState.workOrigin.value === WorkOrigin.Pram}
                                    inputId="work-origin__pram"
                                    label={resources.get("Pram")}
                                    name="work-origin"
                                    onSelect={() => formManager.formStateChangeHandler("workOrigin", WorkOrigin.Pram)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                            </div>
                        </div>

                        {formState.persons.length > 0 ? (
                            <div className="mt-3">
                                <span className="fw-bold">{resources.get("OtherContactInformation")}</span>
                                <ParticipantsList
                                    onAddParticipant={formManager.addParticipantHandler}
                                    onParticipantChange={formManager.updateParticipantHandler}
                                    onRemoveParticipant={formManager.removeParticipantHandler}
                                    resources={resources}
                                    participantListItems={formState.persons}
                                    showUpdateButton={formManager.showUpdateParticipantsButton}
                                    onUpdateButtonClick={formManager.updateParticipantsButtonClickHandlerAsync}
                                    readOnly={!formManager.canManageParticipants}
                                />
                            </div>
                        ) : null}

                        <AdminComment
                            formElement={formState.adminComment}
                            onChange={(s) => formStateChangeHandler("adminComment", s)}
                            readonly={!formManager.canManageAdminComment}
                            onUpdateButtonClick={formManager.updateAdminCommentButtonClickHandlerAsync}
                            showUpdateButton={formManager.showUpdateAdminCommentButton}
                            resources={resources}
                            show={formManager.showAdminComment}
                            containerClassName="mt-4"
                        />

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />

                        <div className="d-flex mt-3">
                            <div className="me-3">
                                {formManager.canCreate ? (
                                    <Button type="submit" disabled={formManager.requestInProgress}>
                                        {resources.get("Send")}
                                    </Button>
                                ) : formManager.canUpdate ? (
                                    <Button type="submit" disabled={formManager.requestInProgress}>
                                        {resources.get("Update")}
                                    </Button>
                                ) : null}
                            </div>
                            {!!storedPermit && formManager.showPrefillEnergyHarbourPermitButton ? (
                                <Link
                                    href={`/Permits/EnergyHarbourPermit?underWaterWorkPermitDocumentId=${storedPermit.id}`}
                                    className="btn btn-primary"
                                >
                                    {resources.get("StartEHPermit")}
                                </Link>
                            ) : null}
                        </div>
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
            <RejectPermitModal
                onHide={() => formManager.setShowRejectPermitModal(false)}
                show={formManager.showRejectPermitModal}
                resources={resources}
                onReject={formManager.rejectPermitHandlerAsync}
            />
        </>
    );
}

export default UnderWaterWorkPermitForm;
